import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { VanityAPI } from '../services/api';
import { FooterConfig, VanityPage } from '../types';
import packageJson from '../package.json';
import CookieConsent from './CookieConsent';
import seoGlobals from '../seo_globals.json';

const Footer: React.FC = () => {
  const [config, setConfig] = useState<FooterConfig | null>(null);
  const [footerLinks, setFooterLinks] = useState<Partial<VanityPage>[]>([]);
  const [showCookieModal, setShowCookieModal] = useState(false);

  useEffect(() => {
    const fetchFooter = async () => {
      try {
        const [conf, nav] = await Promise.all([
          VanityAPI.getFooterConfig(),
          VanityAPI.getNavLinks()
        ]);
        setConfig(conf);
        // Show in footer
        setFooterLinks(nav.filter(l => l.showInFooter));
      } catch (err) {
        console.error("Failed to load footer data:", err);
      }
    };
    fetchFooter();
  }, []);

  if (!config) return null;

  const address = seoGlobals.contact.address;

  return (
    <footer className="bg-vanity-white border-t border-vanity-charcoal/10 py-24">
      {/* Rule 3: Max width 1280px (max-w-screen-xl matches standard tailwind 1280px) */}
      <div className="max-w-screen-xl mx-auto px-8 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-16 md:gap-12">
          <div className="col-span-1 md:col-span-2">
            <h2 className="font-serif text-2xl mb-6 text-vanity-black">{config.title}</h2>
            <p className="font-sans text-vanity-charcoal text-base max-w-sm leading-relaxed mb-10">
              {config.aboutText}
            </p>
            <div className="flex space-x-8">
              {config.instagram && (
                <a href={config.instagram} target="_blank" rel="noreferrer" className="text-vanity-charcoal hover:text-vanity-pink transition-all duration-500 hover:-translate-y-1 p-3 -m-3 inline-flex items-center justify-center w-12 h-12" aria-label="Instagram">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect><path d="M16 11.37A4 4 0 1112.63 8 4 4 0 0116 11.37z"></path><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line></svg>
                </a>
              )}
              {config.facebook && (
                <a href={config.facebook} target="_blank" rel="noreferrer" className="text-vanity-charcoal hover:text-vanity-pink transition-all duration-500 hover:-translate-y-1 p-3 -m-3 inline-flex items-center justify-center w-12 h-12" aria-label="Facebook">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 2h-3a5 5 0 00-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 011-1h3z"></path></svg>
                </a>
              )}
              {config.linkedin && (
                <a href={config.linkedin} target="_blank" rel="noreferrer" className="text-vanity-charcoal hover:text-vanity-pink transition-all duration-500 hover:-translate-y-1 p-3 -m-3 inline-flex items-center justify-center w-12 h-12" aria-label="LinkedIn">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"></path><rect x="2" y="9" width="4" height="12"></rect><circle cx="4" cy="4" r="2"></circle></svg>
                </a>
              )}
            </div>
          </div>
          <div>
            <h3 className="font-sans text-[11px] uppercase tracking-[0.4em] text-vanity-black mb-8 font-black border-b border-vanity-pink/20 pb-2 inline-block">Kontakte</h3>
            <p className="font-sans text-sm text-vanity-charcoal mb-2">Daniela M. Steindl</p>
            <p className="font-serif italic text-base mb-4 text-vanity-pink">Editor-in-Chief</p>
            {/* Rule 38: NAP+C Match. Rendering Address explicitly from global source of truth. */}
            <address className="not-italic font-sans text-vanity-charcoal text-sm mb-4">
              {address.street}<br />
              {address.postalCode} {address.city}, {address.country}
            </address>
            <p className="font-sans text-vanity-charcoal text-sm mb-2">{config.phone}</p>
            <a
              href={`mailto:${config.email}`}
              className="font-serif italic text-base text-vanity-black hover:text-vanity-pink transition-colors inline-block"
            >
              {config.email}
            </a>
          </div>
          <div>
            <h3 className="font-sans text-[11px] uppercase tracking-[0.4em] text-vanity-black mb-8 font-black border-b border-vanity-pink/20 pb-2 inline-block">Kleingedrucktes</h3>
            <ul className="space-y-4">
              {footerLinks.map(page => (
                <li key={page.id}>
                  <Link
                    to={`/${page.slug === 'home' ? '' : page.slug}`}
                    className="font-sans text-vanity-charcoal text-sm hover:text-vanity-pink transition-colors font-bold uppercase tracking-widest inline-flex items-center min-h-[48px] py-3"
                  >
                    {page.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="mt-24 pt-10 border-t border-vanity-charcoal/10 flex flex-col md:flex-row justify-between items-center text-[10px] uppercase tracking-[0.4em] text-vanity-charcoal font-black">
          <div className="flex flex-col md:flex-row items-center gap-6">
            <span>&copy; 2026 VANITY MAGAZINE</span>
            <span className="hidden md:inline text-vanity-pink/40">|</span>
            <span className="bg-vanity-pink/5 px-3 py-1 text-vanity-pink rounded-full border border-vanity-pink/10">v{packageJson.version}</span>
            <span className="hidden md:inline text-vanity-pink/40">|</span>
            <button
              onClick={() => setShowCookieModal(true)}
              className="flex items-center gap-2 text-vanity-charcoal hover:text-vanity-pink transition-colors p-2 -m-2 w-12 h-12 justify-center"
              aria-label="Cookie-Einstellungen"
              title="Cookie-Einstellungen verwalten"
            >
              <span className="text-base">🍪</span>
            </button>
          </div>
          <span className="mt-6 md:mt-0 italic opacity-80">Defined by Aesthetic Excellence</span>
        </div>
      </div>

      {/* Cookie Consent Modal */}
      <CookieConsent isOpen={showCookieModal} onClose={() => setShowCookieModal(false)} />
    </footer>
  );
};

export default Footer;
